/**
 * The audits feature's vocabulary (#240). Domain types come from `@ludus/core`
 * where core has them; what the console alone needs — the state labels, the
 * wire shapes the engine's routes answer — lives here.
 */
export { AUDIT_STAGES } from '@ludus/core'
export type { AuditStage, PipelineKind } from '@ludus/core'

/** `audits.state` as the engine writes it (§5.5). */
export type AuditState =
  | 'draft'
  | 'snapshotting'
  | 'processing'
  | 'paused_quota'
  | 'ready'
  | 'failed'
  | 'archived'
  | 'deleted'

export const AUDIT_STATES: readonly AuditState[] = [
  'draft',
  'snapshotting',
  'processing',
  'paused_quota',
  'ready',
  'failed',
  'archived',
  'deleted',
] as const

/** The states in which the audit is doing something and the page keeps asking (§10.1). */
export const LIVE_STATES: readonly AuditState[] = ['snapshotting', 'processing'] as const

export interface AuditQuota {
  asr_minutes: number
  llm_tokens: number
  amount: number
  currency: string
}

export interface AuditSpend {
  spent: { asr_minutes: number; llm_tokens: number; amount: number; calls: number }
  quota: AuditQuota
  exhausted: 'asr_minutes' | 'llm_tokens' | 'amount' | null
  units: number
  perUnit: number
}

export interface AuditSummary {
  id: string
  title: string
  pipelineIds: string[]
  periodFrom: Date
  periodTo: Date
  cutoffAt: Date
  snapshotAt: Date | null
  state: AuditState
  quota: AuditQuota
  createdAt: Date
  updatedAt: Date
  /** Absent on an audit whose directory is gone. */
  spend: AuditSpend | null
  /** Open review-queue rows; absent on a deleted audit. */
  anomalies: number | null
}

export interface AuditCoverage {
  covered: boolean
  missing: Array<{ pipelineId: string; from: Date; to: Date }>
}

export interface AuditCounters {
  cardsPerPipeline: Record<string, number>
  calls: { withAudio: number; withoutAudio: number }
  contacts: { withPhone: number; withoutPhone: number }
}

export interface AuditLogRow {
  id: string
  actor: string
  action: string
  subject: string | null
  details: Record<string, unknown>
  at: Date
}

export interface AuditDetail {
  audit: AuditSummary
  stagesEnabled: string[]
  providers: { asr: string | null; llm: string | null; llm_model: string | null }
  counters: AuditCounters
  coverage: AuditCoverage
  spend: AuditSpend
  blockedBy: string[]
  log: AuditLogRow[]
}

export type UnitState = 'queued' | 'running' | 'done' | 'failed' | 'skipped' | 'needs_review'

export interface StageSummary {
  stage: string
  level: 'audit' | 'card'
  enabled: boolean
  /** The audit-level job for this stage, when one was asked for. */
  job: { state: string; updatedAt: Date } | null
  units: Record<UnitState, number> & { total: number }
  skipped: Record<string, number>
}

export interface ReviewSummary {
  id: string
  kind: string
  state: 'open' | 'resolved'
  leadId: string | null
}

export interface PipelineOption {
  id: string
  name: string
  kind: 'deal' | 'interaction'
}

export interface BackfillProgress {
  job: { state: string; pages: number; error: string | null } | null
  backfilledFrom: Date | null
  backfilledTo: Date | null
}

export type StageRunScope = 'all' | 'failed' | 'ids'

export interface CreateAuditInput {
  title: string
  pipelineIds: string[]
  periodFrom: Date
  periodTo: Date
  cutoffAt?: Date
  stages?: string[]
  quota?: Partial<AuditQuota>
  providers?: Partial<AuditDetail['providers']>
}

export interface QuotaPatch {
  asr_minutes?: number
  llm_tokens?: number
  amount?: number
  currency?: string
}

/* ------------------------------------------------- the card (#241) */

export interface CardStageChip {
  stage: string
  state: string
  reason: string | null
}

export interface TranscriptSegment {
  idx: number
  startMs: number
  endMs: number
  speaker: string | null
  role: string | null
  text: string
}

export interface CardFact {
  id: string
  key: string
  value: string
  kind: string
  confidence: number | null
  stage: string
  reviewState: string
  evidence: Array<{
    id: string
    kind: string
    refId: string
    startMs: number | null
    endMs: number | null
    quote: string | null
  }>
}

export interface CardPage {
  card: {
    id: string
    pipelineId: string
    pipelineName: string
    kind: 'deal' | 'interaction'
    name: string
    statusName: string | null
    lifecyclePhase: string | null
    assignedManagerId: string | null
    funnel: string | null
    tags: string[]
    createdAt: Date
    ludusLeadId: string | null
    link: { confidence: string | null; method: string | null; linkedBy: string | null }
  }
  stages: CardStageChip[]
  transcript: {
    id: string
    language: string
    durationMs: number | null
    roleConfidence: number | null
    segments: TranscriptSegment[]
  } | null
  audio: Array<{
    id: string
    mime: string | null
    durationMs: number | null
    url: string | null
    /** Set when a subject's erasure removed the bytes; the card says so instead of a player. */
    erasedAt: string | null
  }>
  facts: CardFact[]
  qa: Array<{
    phase: string
    applicability: string
    coverage: string
    fidelityScore: number | null
    explanation: string
  }>
  stageEvidence: Array<{
    id: string
    statusName: string | null
    occurredAt: Date
    verdict: string
    explanation: string | null
    missing: string | null
  }>
  identity: {
    cycle: { id: string; origin: string; productKey: string | null; anomalyFlags: string[] } | null
    customer: { id: string; anomaly: string } | null
    siblings: Array<{
      id: string
      kind: 'deal' | 'interaction'
      name: string
      createdAt: Date
      linkConfidence: string | null
    }>
  }
  history: Array<{ id: string; at: Date; from: string | null; to: string | null; kind: string }>
}

/* ------------------------------------------------ the queue (#241) */

export interface ReviewRow {
  id: string
  kind: string
  subjectType: string
  subjectId: string
  leadId: string | null
  proposal: Record<string, unknown>
  evidence: Record<string, unknown>
  state: 'open' | 'resolved'
  resolution: string | null
  resolvedBy: string | null
  reason: string | null
  ruleVersion: string
  createdAt: Date
  /** 0..1 by the rules' own margin; `null` always needs a decision. */
  confidence: number | null
}

export interface ReviewBudget {
  hours: number
  spentHours: number
  decisions: number
}

/* ------------------------------------------------- the chat (#241) */

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  evidence: Array<{ kind: string; ref_id: string; start_ms?: number; end_ms?: number }>
  inReplyTo: string | null
  polledAt: Date | null
  answeredAt: Date | null
  createdAt: Date
}

export interface BrainStatus {
  online: boolean
  lastPollAt: Date | null
  polls: number
}
